import { useState, useEffect, useMemo } from 'react';
import {
  EntityMembersListCard,
} from '@backstage/plugin-org';
import {
  useApi,
  identityApiRef,
  discoveryApiRef,
  errorApiRef,
} from '@backstage/core-plugin-api';
import { useEntity, catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  TextField,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { Alert } from '@material-ui/lab';

/**
 * 自定义 Group Members 卡片组件
 * 在原有 EntityMembersListCard 基础上添加"添加成员"功能
 */
export const GroupMembersCard: React.FC = () => {
  const { entity } = useEntity();
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const errorApi = useApi(errorApiRef);
  const catalogApi = useApi(catalogApiRef);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [users, setUsers] = useState<Array<{ id: string; name: string; email?: string }>>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSystemManager, setIsSystemManager] = useState(false);

  // 只对 group 类型的实体显示添加成员功能
  const isGroup = entity.kind === 'Group';
  const groupName = entity.metadata.name;

  // 检查用户是否有 system-manager 权限
  useEffect(() => {
    const checkPermission = async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const userOwnershipRefs = identity.ownershipEntityRefs || [];
        
        // 检查用户是否属于 system-manager 组
        const hasSystemManagerRole = userOwnershipRefs.some(ref => 
          ref === 'group:default/system-manager' || 
          ref.toLowerCase() === 'group:default/system-manager'
        );
        
        setIsSystemManager(hasSystemManagerRole);
      } catch {
        // 检查失败时默认不允许（安全起见）
        setIsSystemManager(false);
      }
    };

    checkPermission();
  }, [identityApi]);

  // 获取当前组的成员列表，用于过滤已存在的成员
  const currentMembers = useMemo(() => {
    const members = entity.spec?.members;
    if (Array.isArray(members)) {
      return members.filter((m): m is string => typeof m === 'string');
    }
    return [];
  }, [entity.spec?.members]);

  // 加载所有 User 实体
  useEffect(() => {
    if (dialogOpen) {
      const loadUsers = async () => {
        try {
          setLoadingUsers(true);
          const response = await catalogApi.getEntities({
            filter: {
              kind: 'User',
            },
          });

          // 过滤掉已经在组中的成员
          const allUsers = response.items
            .filter(user => {
              const userRef = `user:default/${user.metadata.name}`;
              return !currentMembers.includes(userRef);
            })
            .map(user => ({
              id: user.metadata.name,
              name: user.metadata.name,
              email: typeof user.spec?.profile === 'object' && user.spec.profile !== null && 'email' in user.spec.profile
                ? String(user.spec.profile.email)
                : undefined,
            }));

          // 按名称排序
          allUsers.sort((a, b) => a.name.localeCompare(b.name));
          setUsers(allUsers);
        } catch (err) {
          setError(err instanceof Error ? err.message : '加载用户列表失败');
        } finally {
          setLoadingUsers(false);
        }
      };

      loadUsers();
    }
  }, [dialogOpen, catalogApi, currentMembers]);

  const handleOpenDialog = () => {
    setDialogOpen(true);
    setSelectedUserId('');
    setError(null);
    setSuccess(false);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUserId('');
    setError(null);
    setSuccess(false);
  };

  const handleAddMember = async () => {
    if (!selectedUserId) {
      setError('请选择要添加的用户');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // 获取后端 API 地址
      // keycloak-member 模块注册在 scaffolder 插件下，路径是 /api/scaffolder/keycloak/...
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      
      // 获取认证 token
      const { token } = await identityApi.getCredentials();

      // 调用后端 API 添加用户到 Keycloak 组
      const response = await fetch(
        `${baseUrl}/keycloak/groups/${encodeURIComponent(groupName)}/members`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ username: selectedUserId }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Failed to add member: ${response.status}`);
      }

      setSuccess(true);
      
      // 延迟关闭对话框，让用户看到成功消息
      // 不自动刷新页面，因为需要等待用户同步周期（约10分钟）
      setTimeout(() => {
        handleCloseDialog();
      }, 3000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '添加成员失败';
      setError(errorMessage);
      errorApi.post(new Error(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* 原有的成员列表卡片 */}
      <Box position="relative">
        <EntityMembersListCard />
        
        {/* 添加成员按钮 - 只对 group 类型且用户有 system-manager 权限时显示 */}
        {isGroup && isSystemManager && (
          <Box position="absolute" top={8} right={8}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleOpenDialog}
            >
              添加成员
            </Button>
          </Box>
        )}
      </Box>

      {/* 添加成员对话框 */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>添加成员到 {groupName}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" paragraph>
            从 Backstage 用户列表中选择要添加的成员，用户将被添加到 Keycloak 组中。
          </Typography>
          
          {error && (
            <Alert severity="error" style={{ marginBottom: 16 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" style={{ marginBottom: 16 }}>
              成员添加成功！将在下次用户同步后生效（约10分钟）。
            </Alert>
          )}

          <Autocomplete
            options={users}
            getOptionLabel={(option) => option.email ? `${option.name} (${option.email})` : option.name}
            value={users.find(u => u.id === selectedUserId) || null}
            onChange={(_, newValue) => setSelectedUserId(newValue?.id || '')}
            loading={loadingUsers}
            disabled={loading || success || loadingUsers}
            renderInput={(params) => {
              let placeholder = '输入用户名或邮箱搜索';
              if (loadingUsers) {
                placeholder = '加载中...';
              } else if (users.length === 0) {
                placeholder = '没有可添加的用户';
              }
              
              return (
                <TextField
                  {...params}
                  label="选择用户"
                  margin="dense"
                  variant="outlined"
                  placeholder={placeholder}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              );
            }}
            filterOptions={(options, { inputValue }) => {
              const searchValue = inputValue.toLowerCase();
              return options.filter(option => 
                option.name.toLowerCase().includes(searchValue) ||
                (option.email && option.email.toLowerCase().includes(searchValue))
              );
            }}
            noOptionsText={loadingUsers ? '加载中...' : '没有匹配的用户'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            取消
          </Button>
          <Button
            onClick={handleAddMember}
            color="primary"
            variant="contained"
            disabled={loading || success || !selectedUserId || loadingUsers}
          >
            {loading ? (
              <>
                <CircularProgress size={16} style={{ marginRight: 8 }} />
                添加中...
              </>
            ) : (
              '添加'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

